import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import api, { categoryName } from "@/lib/api";

const ProductDetail = () => {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [active, setActive] = useState(0);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/products/${slug}`)
            .then(({ data }) => { setP(data); setActive(0); })
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-notfound">
                <div className="overline text-red-600 mb-6">Error / 404</div>
                <h1 className="font-display text-5xl font-black uppercase tracking-tighter mb-8">Machine not found</h1>
                <Link to="/products" className="btn-dark">Back to Catalog</Link>
            </div>
        );
    }
    if (!p) {
        return <div className="max-w-7xl mx-auto px-6 py-32 font-mono text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="product-loading">Loading…</div>;
    }

    return (
        <div data-testid="product-detail-page">
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-6">
                <Link to="/products" className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500 hover:text-gray-900 inline-flex items-center gap-2" data-testid="back-to-catalog">
                    <ArrowLeft size={14} /> Back to Catalog
                </Link>
            </div>

            <section className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 pb-20">
                {/* Gallery */}
                <div className="lg:col-span-7">
                    <div className="product-image-wrap product-image-wrap--detail aspect-[4/3]">
                        {p.images?.[active] && <img src={p.images[active]} alt={p.name} />}
                    </div>
                    {p.images && p.images.length > 1 && (
                        <div className="mt-4 grid grid-cols-5 gap-3">
                            {p.images.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActive(i)}
                                    data-testid={`thumb-${i}`}
                                    className={`product-image-wrap aspect-square ${active === i ? "ring-2 ring-red-600" : ""}`}
                                >
                                    <img src={src} alt={`view ${i + 1}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="lg:col-span-5">
                    <div className="overline text-red-600 mb-5">{categoryName(p.category)}</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[1]" data-testid="product-name">
                        {p.name}
                    </h1>
                    <p className="mt-8 text-gray-700 leading-relaxed">{p.summary}</p>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link
                            to={`/contact?machine=${encodeURIComponent(p.name)}`}
                            className="btn-primary"
                            data-testid="product-quote-cta"
                        >
                            Request a Quote <ArrowRight size={16} />
                        </Link>
                        <Link to="/contact" className="btn-outline" data-testid="product-contact-cta">
                            Talk to Sales
                        </Link>
                    </div>

                    {p.features && p.features.length > 0 && (
                        <div className="mt-12">
                            <div className="overline text-gray-500 mb-5">Key Features</div>
                            <ul className="space-y-3">
                                {p.features.map((f) => (
                                    <li key={f} className="flex gap-3 items-start text-sm">
                                        <Check className="shrink-0 mt-1 text-red-600" size={16} strokeWidth={2.5} />
                                        <span className="text-gray-700">{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </section>

            {/* Description + Specs */}
            <section className="bg-gray-50 border-y border-gray-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-7">
                        <div className="overline text-red-600 mb-4">Description</div>
                        <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mb-6">
                            Engineering Notes
                        </h2>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">{p.description}</p>
                    </div>
                    <div className="md:col-span-5">
                        <div className="overline text-red-600 mb-4">Specifications</div>
                        <div className="border-t border-gray-300" data-testid="spec-table">
                            {p.specs?.map((s, i) => (
                                <div key={i} className="flex justify-between gap-4 py-4 border-b border-gray-300">
                                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-gray-500">{s.label}</span>
                                    <span className="text-sm text-gray-900 font-medium text-right">{s.value}</span>
                                </div>
                            ))}
                            {(!p.specs || p.specs.length === 0) && (
                                <div className="py-6 text-sm text-gray-500">Detailed specs available on request.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductDetail;
